import logger from '@/lib/clientLogger';
import { FeatureFlags } from '@/components/FeatureFlagsContext';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import AmpliHelper from '@/utilities/ampliAnalyticsHelper';
import { CLIENT_FEATURE_FLAGS } from '@/constants';
import getVertical from './verticalUtils';

/**
 * Posts an Amplitude event with the specified data.
 * Additional fields can be sent using the `extraData` parameter. The fields in that object will be
 * added to the `data` object that will be posted to Amplitude.
 * The minimal data sent is the following:
 * {
 *   name: [type],
 *   data: {
 *     cta_clicked: cta,
 *     final_step: isFinalStep, // default false
 *     vertical: getVertical() // gets vertical from URL
 *     [(job|enrollment)_flow]: flow,
 *     [(job|enrollment)_step]: step,
 *   }
 * }
 * @see AnalyticsHelper.logEvent
 * @param type Event type. Currently 'Member Enrolled' or 'Job Posted'
 * @param step Flow step the event was generated in.
 * @param flow Flow name the event was generated in.
 * @param extraData Any additional key-value pairs that should be sent besides the default data
 * @param cta CTA clicked. Defaults to 'next'
 * @param isFinal boolean indicating if this is the flow's last step. Defaults to 'false'
 */

export type MemberEnrolledEvent = 'Member Enrolled';
export type JobPostedEvent = 'Job Posted';
export type EnrollmentStep =
  | 'Grade'
  | 'Modes'
  | 'Dates'
  | 'Intent'
  | 'Details'
  | 'Tasks'
  | 'Service type'
  | 'Location'
  | 'Caregiver tasks'
  | 'Age level'
  | 'Email'
  | 'First and Last Name'
  | 'Password'
  | 'day care account creation - name'
  | 'day care account creation - contact'
  | 'SingleEnrollment'
  | 'Additional Verticals';

export type JobStep =
  | 'PAJ - Start'
  | 'PAJ - Schedule'
  | 'PAJ - Additional Needs'
  | 'PAJ - Ideal caregiver'
  | 'PAJ - Extra info'
  | 'PAJ - Goals'
  | 'PAJ - Finish';

export type EventType = MemberEnrolledEvent | JobPostedEvent;
export type EventStep = EnrollmentStep | JobStep;

export function logCareEvent(
  type: JobPostedEvent,
  step: JobStep,
  extraData?: Record<string, any>,
  cta?: string,
  isFinal?: boolean,
  flow?: string
): void;
export function logCareEvent(
  type: MemberEnrolledEvent,
  step: EnrollmentStep,
  extraData?: Record<string, any>,
  cta?: string,
  isFinal?: boolean,
  flow?: string
): void;
export function logCareEvent(
  type: EventType,
  step: EventStep,
  extraData: Record<string, any> = {},
  cta: string = 'Next',
  isFinal: boolean = false,
  flow: string = 'MW VHP'
): void {
  let stepKey;
  let flowKey;
  switch (type) {
    case 'Member Enrolled':
      stepKey = 'enrollment_step';
      flowKey = 'enrollment_flow';
      break;
    case 'Job Posted':
      stepKey = 'job_step';
      flowKey = 'job_flow';
      break;
    default:
      throw new Error(`Invalid event type ${type}`);
  }
  AnalyticsHelper.logEvent({
    name: type,
    data: {
      cta_text: cta,
      final_step: isFinal,
      vertical: getVertical(),
      [stepKey]: step,
      [flowKey]: flow,
      ...extraData,
    },
  });
}

// TODO: Replace _app.tsx code with these methods (setDataByURL and logScreenViewedEvent)
// JIRA: https://carecom.atlassian.net/browse/GROW-8497

// If you need more custom data for the screen viewed event, just add the type here

interface ScreenViewedCustomData {
  // eslint-disable-next-line camelcase
  screen_name?: string;
  // eslint-disable-next-line camelcase
  screen_template?: string;
  // eslint-disable-next-line camelcase
  need_email?: boolean;
}

export const setDataByURL = (url: string): ScreenViewedCustomData => {
  if (url === '/seeker/sc') {
    return {
      screen_name: 'seeker.enroll.consolidatedFlow.seniorcare.updateGuided.intro',
      screen_template: 'seeker.enroll.consolidatedFlow.seniorcare.updateGuided.intro',
    };
  }

  return {};
};

export function logScreenViewedEvent(
  customData: ScreenViewedCustomData,
  url: string,
  ldFlags: FeatureFlags
) {
  const dataByUrl = setDataByURL(url);
  const data = { ...dataByUrl, ...customData };

  AnalyticsHelper.logEvent({
    name: 'Screen Viewed',
    data: {
      ...data,
      referer: document.referrer,
    },
  });

  if (AmpliHelper.useAmpli(ldFlags) && url === '/seeker/sc') {
    AmpliHelper.ampli.screenViewedTypeOfSeniorCareNeed({
      ...AmpliHelper.getCommonData(),
      ...data,
      referer: document.referrer,
    });
  }

  if (
    url.startsWith('/seeker') &&
    ldFlags[CLIENT_FEATURE_FLAGS.GROWTH_SEEKER_SCREEN_VIEWED_SPLUNK]?.value
  ) {
    logger.info({
      ...data,
      event: 'Screen Viewed',
      referer: document.referrer,
    });
  }
}
